import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Borefield } from '../types';

// View state types
export type BorefieldView =
  | 'list'
  | 'detail'
  | 'create'
  | 'edit'
  | 'add-bore';

export interface ViewParams {
  borefieldId?: string;
  boreId?: string;
  jobId?: string;
  initialTab?: string;
}

export interface BorefieldFilters {
  search: string;
  status: string;
  sortBy: 'name' | 'location' | 'boreCount' | 'status' | 'createdAt';
  sortOrder: 'asc' | 'desc';
}

export interface BorefieldUIState {
  // Current view
  currentView: BorefieldView;
  viewParams: ViewParams;

  // List view state
  filters: BorefieldFilters;
  selectedBorefieldIds: string[];

  // Detail view state
  activeTab: string;
  expandedBoreIds: string[];

  // Local borefield data (for offline/demo mode)
  localBorefields: Borefield[];
  useLocalData: boolean;
}

const initialState: BorefieldUIState = {
  currentView: 'list',
  viewParams: {},

  filters: {
    search: '',
    status: '',
    sortBy: 'name',
    sortOrder: 'asc',
  },
  selectedBorefieldIds: [],

  activeTab: 'overview',
  expandedBoreIds: [],

  localBorefields: [],
  useLocalData: true, // Start with local data until API is connected
};

const borefieldSlice = createSlice({
  name: 'borefield',
  initialState,
  reducers: {
    // View navigation
    setView(state, action: PayloadAction<{ view: BorefieldView; params?: ViewParams }>) {
      state.currentView = action.payload.view;
      state.viewParams = action.payload.params ?? {};
    },

    // Filters
    setSearchFilter(state, action: PayloadAction<string>) {
      state.filters.search = action.payload;
    },
    setStatusFilter(state, action: PayloadAction<string>) {
      state.filters.status = action.payload;
    },
    setSortBy(state, action: PayloadAction<BorefieldFilters['sortBy']>) {
      state.filters.sortBy = action.payload;
    },
    setSortOrder(state, action: PayloadAction<'asc' | 'desc'>) {
      state.filters.sortOrder = action.payload;
    },
    clearFilters(state) {
      state.filters = initialState.filters;
    },

    // Selection
    selectBorefield(state, action: PayloadAction<string>) {
      if (!state.selectedBorefieldIds.includes(action.payload)) {
        state.selectedBorefieldIds.push(action.payload);
      }
    },
    deselectBorefield(state, action: PayloadAction<string>) {
      state.selectedBorefieldIds = state.selectedBorefieldIds.filter(
        (id) => id !== action.payload
      );
    },
    toggleBorefieldSelection(state, action: PayloadAction<string>) {
      const index = state.selectedBorefieldIds.indexOf(action.payload);
      if (index >= 0) {
        state.selectedBorefieldIds.splice(index, 1);
      } else {
        state.selectedBorefieldIds.push(action.payload);
      }
    },
    clearSelection(state) {
      state.selectedBorefieldIds = [];
    },

    // Detail view
    setActiveTab(state, action: PayloadAction<string>) {
      state.activeTab = action.payload;
    },
    toggleBoreExpanded(state, action: PayloadAction<string>) {
      const index = state.expandedBoreIds.indexOf(action.payload);
      if (index >= 0) {
        state.expandedBoreIds.splice(index, 1);
      } else {
        state.expandedBoreIds.push(action.payload);
      }
    },
    expandAllBores(state, action: PayloadAction<string[]>) {
      state.expandedBoreIds = action.payload;
    },
    collapseAllBores(state) {
      state.expandedBoreIds = [];
    },

    // Local data management (for demo/offline mode)
    setLocalBorefields(state, action: PayloadAction<Borefield[]>) {
      // Cast to satisfy exactOptionalPropertyTypes with Immer's WritableDraft
      state.localBorefields = action.payload as typeof state.localBorefields;
    },
    addLocalBorefield(state, action: PayloadAction<Borefield>) {
      const existingIndex = state.localBorefields.findIndex(
        (bf) => bf.id === action.payload.id
      );
      if (existingIndex >= 0) {
        state.localBorefields[existingIndex] = action.payload as typeof state.localBorefields[number];
      } else {
        state.localBorefields.push(action.payload as typeof state.localBorefields[number]);
      }
    },
    updateLocalBorefield(state, action: PayloadAction<Borefield>) {
      const index = state.localBorefields.findIndex(
        (bf) => bf.id === action.payload.id
      );
      if (index >= 0) {
        state.localBorefields[index] = action.payload as typeof state.localBorefields[number];
      }
    },
    deleteLocalBorefield(state, action: PayloadAction<string>) {
      state.localBorefields = state.localBorefields.filter(
        (bf) => bf.id !== action.payload
      );
    },
    setUseLocalData(state, action: PayloadAction<boolean>) {
      state.useLocalData = action.payload;
    },
  },
});

export const {
  setView,
  setSearchFilter,
  setStatusFilter,
  setSortBy,
  setSortOrder,
  clearFilters,
  selectBorefield,
  deselectBorefield,
  toggleBorefieldSelection,
  clearSelection,
  setActiveTab,
  toggleBoreExpanded,
  expandAllBores,
  collapseAllBores,
  setLocalBorefields,
  addLocalBorefield,
  updateLocalBorefield,
  deleteLocalBorefield,
  setUseLocalData,
} = borefieldSlice.actions;

export const borefieldReducer = borefieldSlice.reducer;

// Selectors
export const selectCurrentView = (state: { borefield: BorefieldUIState }) =>
  state.borefield.currentView;
export const selectViewParams = (state: { borefield: BorefieldUIState }) =>
  state.borefield.viewParams;
export const selectFilters = (state: { borefield: BorefieldUIState }) =>
  state.borefield.filters;
export const selectSelectedBorefieldIds = (state: { borefield: BorefieldUIState }) =>
  state.borefield.selectedBorefieldIds;
export const selectActiveTab = (state: { borefield: BorefieldUIState }) =>
  state.borefield.activeTab;
export const selectExpandedBoreIds = (state: { borefield: BorefieldUIState }) =>
  state.borefield.expandedBoreIds;
export const selectLocalBorefields = (state: { borefield: BorefieldUIState }) =>
  state.borefield.localBorefields;
export const selectUseLocalData = (state: { borefield: BorefieldUIState }) =>
  state.borefield.useLocalData;
